import History from 'history';

import { UnreachableFunction } from 'ui/base/types';

import { generateUUID } from 'services/base/uuid';
import { OrderedMap } from 'services/base/types';

import { BasePath, PagePath, pagePathToPathString } from './page_path';

const HISTORY_CHANGE_TIMEOUT = 20;

export class HistoryService {
  static history: History.Location[];

  static blockers: OrderedMap<string, History.TransitionPromptHook>;

  static initialize(url: History.Location) {
    const sessionStore = sessionStorage.getItem('app-history');
    HistoryService.history = sessionStore ? JSON.parse(sessionStore) : [url];
    HistoryService.blockers = new OrderedMap();
  }

  static listener(url: History.Location, action: History.Action) {
    switch(action) {
      case 'PUSH': {
        // When the user switches from another app to our app, the system
        // sometimes pushes the previous top URL to the history again so we
        // need to suppress it in order to avoid duplicate entries.
        const len = HistoryService.history.length;
        const top = len > 0 ? HistoryService.history[len - 1] : null;
        if (!top || top.key !== url.key) HistoryService.history.push(url);
        break;
      }
      case 'POP': {
        HistoryService.history.pop();
        break;
      }
      case 'REPLACE': {
        HistoryService.history.pop();
        HistoryService.history.push(url);
        break;
      }
      default:
        UnreachableFunction(action);
    }
    sessionStorage.setItem('app-history', JSON.stringify(HistoryService.history));
  }

  static blocker(url: History.Location, action: History.Action) {
    for (const blocker of HistoryService.blockers.values.reverse()) {
      if (blocker(url, action) === false) return false;
    }
  }

  static canGoBack(): boolean {
    return HistoryService.history.length > 1;
  }

  static clear(reactHistory: History.History): boolean {
    const shouldPop = (_location: History.Location) => true;
    return clearWhileCondition(reactHistory, shouldPop);
  }

  static clearWhile(reactHistory: History.History, pathTypes: readonly PagePath[]): boolean {
    // clearWhile returns true if it managed to clear everything
    // It returns false if the last item still matches the condition
    const paths = pathTypes.map(pagePathToPathString);
    const shouldPop = (location: History.Location) => {
      return paths.indexOf(location.pathname) >= 0;
    };
    return clearWhileCondition(reactHistory, shouldPop);
  }

  static clearUntil(reactHistory: History.History, pathTypes: readonly PagePath[]): boolean {
    // clearUntil returns true if it managed to clear everything
    // It returns false if the last item still DOES NOT match the condition
    // so we had to stop for external reasons
    const paths = pathTypes.map(pagePathToPathString);
    const shouldPop = (location: History.Location) => {
      return paths.indexOf(location.pathname) < 0;
    };
    return clearWhileCondition(reactHistory, shouldPop);
  }

  static replace(reactHistory: History.History, pathType: PagePath): Promise<void> {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        reactHistory.replace(pagePathToPathString(pathType));
        resolve();
      }, HISTORY_CHANGE_TIMEOUT);
    });
  }

  static push(reactHistory: History.History, pathType: PagePath): Promise<void> {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        reactHistory.push(pagePathToPathString(pathType));
        resolve();
      }, HISTORY_CHANGE_TIMEOUT);
    });
  }

  static block(blocker: History.TransitionPromptHook) {
    const uuid = generateUUID();
    HistoryService.blockers.set(uuid, blocker);
    return () => { HistoryService.blockers.delete(uuid); };
  }
}

function clearWhileCondition(
  reactHistory: History.History,
  shouldPop: (location: History.Location) => boolean
): boolean {
  for (let i=HistoryService.history.length - 1; i >= 0; i--) {
    const location = HistoryService.history[i];
    if (!shouldPop(location)) {
      return true;
    } else if (i > 0) {
      reactHistory.goBack();
    } else {
      return false;
    }
  }
  return true;
}

export const ADD_PANGOLIN_PAGE_TYPES: readonly PagePath[] = [
  { kind: BasePath.ADD_PANGOLIN_SETUP },
  { kind: BasePath.ADD_PANGOLIN_POWER },
  { kind: BasePath.ADD_PANGOLIN_METHOD },
  { kind: BasePath.ADD_PANGOLIN_MNEMONIC },
];
